import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import {
  adminListCollections, adminCreateCollection, adminUpdateCollection, adminDeleteCollection,
  adminUploadImage,
} from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";

const slugify = (s) => (s || "").toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const emptyForm = { id: null, name: "", slug: "", banner_image: null, description: "", status: "draft" };

export default function AdminCollectionsPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try { setItems(await adminListCollections()); }
    catch { toast.error("Failed to load collections"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => setEditing({ ...emptyForm });
  const startEdit = (c) => setEditing({ ...c });
  const cancel = () => setEditing(null);

  const onSave = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    const payload = {
      name: editing.name.trim(),
      slug: (editing.slug || slugify(editing.name)).trim(),
      banner_image: editing.banner_image || null,
      description: editing.description || "",
      status: editing.status || "draft",
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdateCollection(editing.id, payload);
      else await adminCreateCollection(payload);
      toast.success(editing.id ? "Collection updated" : "Collection created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete collection "${c.name}"? If products are assigned, they will be unassigned.`)) return;
    try {
      await adminDeleteCollection(c.id, true);
      toast.success("Collection deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onBannerUpload = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      // Backend returns "/api/uploads/..." — prepend the origin so the img loads regardless of route context.
      setEditing((e) => ({ ...e, banner_image: url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-collections-page">
      <AdminHeader active="collections" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Catalogue</div>
            <h1 className="admin-heading">Collections</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-collection-btn">+ New Collection</button>
          )}
        </div>

        {editing ? (
          <CollectionForm
            form={editing}
            saving={saving}
            onChange={setEditing}
            onSave={onSave}
            onCancel={cancel}
            onBannerUpload={onBannerUpload}
          />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-collections-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No collections yet. Click "New Collection" to create one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Name</th><th>Slug</th><th>Status</th><th>Description</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} data-testid={`col-row-${c.slug}`}>
                      <td><strong>{c.name}</strong></td>
                      <td><code style={{ fontSize: 12 }}>{c.slug}</code></td>
                      <td><span className={`admin-badge admin-badge-${c.status}`}>{c.status}</span></td>
                      <td style={{ maxWidth: 320, opacity: 0.75 }}>{c.description?.slice(0, 80) || "—"}</td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(c)} data-testid={`col-edit-${c.slug}`}>Edit</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(c)} data-testid={`col-del-${c.slug}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function CollectionForm({ form, saving, onChange, onSave, onCancel, onBannerUpload }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  const bannerSrc = form.banner_image ? (form.banner_image.startsWith("http") ? form.banner_image : `${process.env.REACT_APP_BACKEND_URL}${form.banner_image}`) : null;
  return (
    <div className="admin-form" data-testid="admin-collection-form">
      <div className="admin-form-row">
        <label>Collection Name<span className="req">*</span></label>
        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => { if (!form.slug) set("slug", slugify(form.name)); }} data-testid="col-form-name" />
      </div>
      <div className="admin-form-row">
        <label>URL Slug</label>
        <input type="text" value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="auto-generated from name" data-testid="col-form-slug" />
        <div className="admin-form-hint">Public URL: /{form.slug || "your-slug"}</div>
      </div>
      <div className="admin-form-row">
        <label>Description</label>
        <textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="col-form-desc" />
      </div>
      <div className="admin-form-row">
        <label>Banner Image</label>
        <input type="file" accept="image/*" onChange={(e) => onBannerUpload(e.target.files?.[0])} data-testid="col-form-banner" />
        {bannerSrc && (
          <div className="admin-image-preview">
            <img src={bannerSrc} alt="banner" />
            <button type="button" className="admin-link-btn admin-link-danger" onClick={() => set("banner_image", null)}>Remove</button>
          </div>
        )}
      </div>
      <div className="admin-form-row">
        <label>Status</label>
        <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="col-form-status">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
      <div className="admin-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="col-form-cancel">Cancel</button>
        <button className="admin-export-btn" onClick={onSave} disabled={saving} data-testid="col-form-save">
          {saving ? "Saving…" : form.id ? "Save Changes" : "Create Collection"}
        </button>
      </div>
    </div>
  );
}
